import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getConsumptionById,
  updateConsumption,
  getConsumptionsByBuildingId
} from '../../Fetch/fetchElectricVehicleConsumption';
import styles from './ElectricVehicleForm.module.css';

const ElectricVehicleEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    date: '',
    counter: '',
    previousCounter: '', // מונה חודש קודם
    powerConsumption: '',
    parkingNumber: '',
    phoneNumber: '',
    totalPowerConsumptionPerMonth: '',
    totalMoneyNeedPay: 0,
    dateCharged: null,
    totalMoneyPaid: 0,
    apartmentNumber: '',
    userID: '',
    userName: ''
  });

  // טעינת הנתונים הקיימים
  useEffect(() => {
    fetchConsumption();
  }, [id]);

  // חישוב אוטומטי של totalMoneyNeedPay - (מונה נוכחי - מונה קודם) × מחיר לקילוואט
  useEffect(() => {
    const counter = parseFloat(formData.counter) || 0;
    const previousCounter = parseFloat(formData.previousCounter);
    const powerConsumption = parseFloat(formData.powerConsumption) || 0;

    // אם אין מונה קודם (ריק או 0), נקח רק את המונה הנוכחי
    let consumption;
    if (!previousCounter || previousCounter === '' || previousCounter === 0) {
      consumption = counter;
    } else {
      // אם יש מונה קודם, נחשב: מונה נוכחי - מונה קודם
      consumption = counter - parseFloat(previousCounter);
    }

    const calculatedAmount = consumption * powerConsumption;

    setFormData(prev => ({
      ...prev,
      totalPowerConsumptionPerMonth: consumption,
      totalMoneyNeedPay: calculatedAmount
    }));
  }, [formData.counter, formData.previousCounter, formData.powerConsumption]);

  const fetchConsumption = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getConsumptionById(id);

      // חישוב המונה הקודם מהרשומה הנוכחית
      const previousCounter = data.counter - data.totalPowerConsumptionPerMonth;

      setFormData({
        date: data.date.split('T')[0],
        counter: data.counter,
        previousCounter: previousCounter,
        powerConsumption: data.powerConsumption,
        parkingNumber: data.parkingNumber,
        phoneNumber: data.phoneNumber,
        totalPowerConsumptionPerMonth: data.totalPowerConsumptionPerMonth,
        totalMoneyNeedPay: data.totalMoneyNeedPay,
        dateCharged: data.dateCharged ? data.dateCharged.split('T')[0] : null,
        totalMoneyPaid: data.totalMoneyPaid,
        apartmentNumber: data.apartmentNumber,
        userID: data.userID,
        userName: data.userName,
        buildingID: data.buildingID
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // המרת הערכים למספרים
      const dataToSubmit = {
        ...formData,
        counter: parseFloat(formData.counter),
        powerConsumption: parseFloat(formData.powerConsumption),
        parkingNumber: parseInt(formData.parkingNumber),
        totalPowerConsumptionPerMonth: parseFloat(formData.totalPowerConsumptionPerMonth),
        totalMoneyNeedPay: parseFloat(formData.totalMoneyNeedPay),
        totalMoneyPaid: parseFloat(formData.totalMoneyPaid),
        apartmentNumber: parseInt(formData.apartmentNumber),
        userID: parseInt(formData.userID)
      };

      await updateConsumption(id, dataToSubmit);
      navigate('/app/electric-vehicle');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/app/electric-vehicle');
  };

  if (loading && !formData.parkingNumber) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>טוען נתונים...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>עריכת רשומת צריכת חשמל</h1>
        <button className={styles.backButton} onClick={handleCancel}>
          חזור לרשימה
        </button>
      </div>

      {error && (
        <div className={styles.error}>
          <p>{error}</p>
        </div>
      )}

      <div className={styles.formContainer}>
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* פרטי רכב */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>פרטי רכב</h2>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>תאריך: <span className={styles.required}>*</span></label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>מספר חניה: <span className={styles.required}>*</span></label>
                <input
                  type="number"
                  name="parkingNumber"
                  value={formData.parkingNumber}
                  onChange={handleInputChange}
                  placeholder="1"
                  required
                />
              </div>
            </div>
          </div>

          {/* פרטי דייר */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>פרטי דייר</h2>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>מספר דירה: <span className={styles.required}>*</span></label>
                <input
                  type="number"
                  name="apartmentNumber"
                  value={formData.apartmentNumber}
                  onChange={handleInputChange}
                  placeholder="1"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>שם: <span className={styles.required}>*</span></label>
                <input
                  type="text"
                  name="userName"
                  value={formData.userName}
                  onChange={handleInputChange}
                  placeholder="שם מלא"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>טלפון: <span className={styles.required}>*</span></label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="050-1234567"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>מזהה משתמש: <span className={styles.required}>*</span></label>
                <input
                  type="number"
                  name="userID"
                  value={formData.userID}
                  onChange={handleInputChange}
                  placeholder="1"
                  required
                />
              </div>
            </div>
          </div>

          {/* צריכת חשמל וחיוב */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>צריכת חשמל וחיוב</h2>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>מונה חודש קודם (kWh):</label>
                <input
                  type="number"
                  step="0.01"
                  name="previousCounter"
                  value={formData.previousCounter}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  style={{ backgroundColor: '#f5f5f5' }}
                />
                <small>קריאת המונה מהחודש הקודם</small>
              </div>

              <div className={styles.formGroup}>
                <label>מונה נוכחי (kWh): <span className={styles.required}>*</span></label>
                <input
                  type="number"
                  step="0.01"
                  name="counter"
                  value={formData.counter}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  required
                />
                <small>קריאת המונה החדשה</small>
              </div>

              <div className={styles.formGroup}>
                <label>מחיר לקילוואט (₪): <span className={styles.required}>*</span></label>
                <input
                  type="number"
                  step="0.01"
                  name="powerConsumption"
                  value={formData.powerConsumption}
                  onChange={handleInputChange}
                  placeholder="0.58"
                  required
                />
                <small>התעריף הנוכחי של חברת החשמל</small>
              </div>

              <div className={styles.formGroup}>
                <label>סה"כ צריכה (kWh):</label>
                <input
                  type="number"
                  step="0.01"
                  name="totalPowerConsumptionPerMonth"
                  value={formData.totalPowerConsumptionPerMonth}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5' }}
                  placeholder="0.00"
                />
                <small>מונה נוכחי - מונה קודם</small>
              </div>
            </div>

            <div className={styles.calculationBox}>
              <div className={styles.calculationItem}>
                <span>מונה נוכחי:</span>
                <strong>{parseFloat(formData.counter || 0).toFixed(2)}</strong>
              </div>
              <div className={styles.calculationSymbol}>-</div>
              <div className={styles.calculationItem}>
                <span>מונה קודם:</span>
                <strong>{parseFloat(formData.previousCounter || 0).toFixed(2)}</strong>
              </div>
              <div className={styles.calculationSymbol}>=</div>
              <div className={styles.calculationItem}>
                <span>צריכה:</span>
                <strong>{parseFloat(formData.totalPowerConsumptionPerMonth || 0).toFixed(2)} kWh</strong>
              </div>
              <div className={styles.calculationSymbol}>×</div>
              <div className={styles.calculationItem}>
                <span>מחיר לקילוואט:</span>
                <strong>₪{parseFloat(formData.powerConsumption || 0).toFixed(2)}</strong>
              </div>
              <div className={styles.calculationSymbol}>=</div>
              <div className={styles.calculationResult}>
                <span>סה"כ לתשלום:</span>
                <strong>₪{formData.totalMoneyNeedPay.toFixed(2)}</strong>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>סכום ששולם (₪):</label>
                <input
                  type="number"
                  step="0.01"
                  name="totalMoneyPaid"
                  value={formData.totalMoneyPaid}
                  onChange={handleInputChange}
                  placeholder="0.00"
                />
                <small>סכום שכבר שולם</small>
              </div>

              <div className={styles.formGroup}>
                <label>תאריך חיוב:</label>
                <input
                  type="date"
                  name="dateCharged"
                  value={formData.dateCharged || ''}
                  onChange={handleInputChange}
                />
                <small>תאריך החיוב בפועל</small>
              </div>

              <div className={styles.formGroup}>
                <div className={styles.balanceBox}>
                  <span>
                    {(formData.totalMoneyNeedPay - formData.totalMoneyPaid) > 0
                      ? 'יתרה לתשלום:'
                      : 'החזר לדייר:'}
                  </span>
                  <strong className={
                    (formData.totalMoneyNeedPay - formData.totalMoneyPaid) > 0
                      ? styles.unpaid
                      : styles.paid
                  }>
                    ₪{Math.abs(formData.totalMoneyNeedPay - formData.totalMoneyPaid).toFixed(2)}
                  </strong>
                </div>
              </div>
            </div>
          </div>

          {/* כפתורי פעולה */}
          <div className={styles.formActions}>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={loading}
            >
              {loading ? 'שומר...' : 'עדכן רשומה'}
            </button>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={loading}
            >
              ביטול
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ElectricVehicleEdit;
